import { useEffect, useState, useMemo } from "react";
import { NMAxiosInst } from "@neumetric/blueprint-lib/store";
import {
  NMStatsTable,
  BarChart,
  Col,
  DonutChart,
  NMStatsCards,
  NMWaitIndicator,
} from "@neumetric/blueprint-lib/components";
import { ExportSection } from "../ExportSection";

const MODULE_KEY = "Documenter_Documentation_Status";

const DeptColumns = [
  { attribute: "department", title: "" },
  {
    attribute: "overall",
    title: "OVERALL",
    id: "Overall",
    overallFlag: true,
    highlight: { header: true, cell: true },
    width: "16%",
  },
  { attribute: "published", title: "Published", id: "CLOSED" },
  { attribute: "approved", title: "Approved", id: "COMPLETE" },
  { attribute: "reviewed", title: "Review", id: "REVIEW" },
  { attribute: "draft", title: "Draft", id: "STARTED" },
];

export function DocumentorStats(props) {
  const { departments, user, mode, dateRange } = props;
  const [responseData, setResponseData] = useState(null);
  const [deptWiseRows, setDeptWiseRows] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const effectiveRangeId =
    typeof dateRange === "string" ? dateRange : dateRange?.id || "last_365_days";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await NMAxiosInst.post("/documents/central_dashboard", {
          date_range_type: effectiveRangeId,
        });
        if (!cancelled) setResponseData(res);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching documenter stats:", error);
          setResponseData({});
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveRangeId]);

  useEffect(() => {
    if (responseData === null) return;
    const tableDataToDisplay = tranformDataIntoTable(
      responseData,
      user,
      departments
    );
    setDeptWiseRows(tableDataToDisplay || []);
    setIsLoading(false);
  }, [responseData, user, departments]);

  const SERIES_ORDER = ["DRAFT", "REVIEW", "APPROVED", "PUBLISHED"];
  const toNumber = (v) => (v === "-" || v == null ? 0 : Number(v));

  function buildChartDatasets(deptWiseRows, departments, brandname) {
    const byDept = new Map(
      (deptWiseRows || [])
        .filter((r) => r && r.department && r.department !== brandname)
        .map((r) => [r.department, r])
    );
    const axisDepts = (departments || [])
      .map((d) => (d && (d.id ?? d.name)) || d)
      .filter(Boolean)
      .filter((d) => d !== brandname)
      .sort((a, b) => a.localeCompare(b));

    return Object.fromEntries(
      axisDepts
        .map((dept) => {
          const r = byDept.get(dept) || {};
          const valuesByKey = {
            PUBLISHED: toNumber(r.published),
            APPROVED: toNumber(r.approved),
            REVIEW: toNumber(r.reviewed),
            DRAFT: toNumber(r.draft),
          };
          const colorByKey = {
            PUBLISHED: "CLOSED",
            APPROVED: "COMPLETE",
            REVIEW: "REVIEW",
            DRAFT: "DRAFT",
          };
          const labelByKey = {
            PUBLISHED: "PUBLISHED",
            APPROVED: "APPROVED",
            REVIEW: "REVIEW",
            DRAFT: "DRAFT",
          };
          return {
            dept,
            valuesByKey,
            dataset: SERIES_ORDER.map((k) => ({
              key: colorByKey[k],
              label: labelByKey[k],
              value: valuesByKey[k],
            })),
          };
        })
        .filter(({ valuesByKey }) => {
          // Only include departments with at least one non-zero value
          return Object.values(valuesByKey).some((value) => value !== 0);
        })
        .map(({ dept, dataset }) => [dept, dataset])
    );
  }

  const chartDatasets = buildChartDatasets(
    deptWiseRows,
    departments,
    user.org.brandname
  );
  const safeNum = (v) => (v === "-" || v == null ? 0 : Number(v));
  const brandRow = deptWiseRows?.[0] ?? null;

  const totalDocs = brandRow ? safeNum(brandRow.overall) : 0;
  const completionPct =
    brandRow && totalDocs > 0
      ? Math.round((safeNum(brandRow.published) * 100) / totalDocs)
      : 0;

  const barChartHeight = useMemo(() => {
    if (Object.keys(chartDatasets).length > 0) {
      const perRowHeight = 55; // based on your 425px for 15 rows
      const calculated = Object.keys(chartDatasets).length * perRowHeight;
      return calculated + 100;
    }
    return 200;
  }, [chartDatasets]);

  const donutDatasets = useMemo(() => {
    const loading = deptWiseRows === null;
    return [
      {
        key: "CLOSED",
        value: loading ? null : brandRow ? safeNum(brandRow.published) : 0,
        label: "Published",
        border: false,
        slotOrder: 3,
        legendOrder: 0,
        textColor: "#FFF",
      },
      {
        key: "APPROVED",
        value: loading ? null : brandRow ? safeNum(brandRow.approved) : 0,
        label: "Approved",
        border: true,
        slotOrder: 2,
        legendOrder: 1,
      },
      {
        key: "REVIEW",
        value: loading ? null : brandRow ? safeNum(brandRow.reviewed) : 0,
        label: "Review",
        border: true,
        slotOrder: 1,
        legendOrder: 2,
      },
      {
        key: "DRAFT",
        value: loading ? null : brandRow ? safeNum(brandRow.draft) : 0,
        label: "Draft",
        border: true,
        slotOrder: 0,
        legendOrder: 3,
      },
    ];
  }, [brandRow, deptWiseRows]);

  const donutDatasetToTable = (allDonutSets = []) => {
    const totals = {
      CLOSED: 0,
      APPROVED: 0,
      REVIEW: 0,
      DRAFT: 0,
    };

    allDonutSets.forEach((d) => {
      const key = d.key?.toUpperCase();
      if (key in totals) totals[key] += Number(d.value) || 0;
    });

    const DonutColumns = [
      { title: "Published", attribute: "CLOSED" },
      { title: "Approved", attribute: "APPROVED" },
      { title: "Review", attribute: "REVIEW" },
      { title: "Draft", attribute: "DRAFT" },
      { title: "Completion (%)", attribute: "COMPLETION" },
    ];
    const DonutRows = [
      {
        PUBLISHED: totals.CLOSED,
        APPROVED: totals.APPROVED,
        REVIEW: totals.REVIEW,
        DRAFT: totals.DRAFT,
        COMPLETION: `${completionPct}%`,
      },
    ];

    return { DonutColumns, DonutRows };
  };
  const { DonutColumns, DonutRows } = donutDatasetToTable(donutDatasets);
  if (isLoading) return <NMWaitIndicator />;

  return (
    <>
      {(mode === "full" || mode === "export_everything") && (
        <div className="o-auto" style={{ padding: "20px 0 0 20px" }}>
          <div style={{ padding: "20px 0 0 20px", color: "#218282" }}>
            <div className="ff-exo2 fw-700 fs-16 line-16">Documentation</div>
            <div className="ff-exo2 fw-400 fs-14 line-16">
              {user?.org?.brandname}
            </div>
          </div>

          <ExportSection
            moduleKey={MODULE_KEY}
            index={1}
            style={{ display: "inline-block", width: 700 }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 20,
                padding: "0px 20px 0 20px",
                flexWrap: "wrap",
              }}
            >
              <Col style={{ flex: "0 0 260px" }}>
                {deptWiseRows && (
                  <NMStatsCards
                    cards={[
                      {
                        key: "pub",
                        label: "Published",
                        value: brandRow
                          ? safeNum(brandRow.published) || "-"
                          : "-",
                        classes: {
                          root: "bg-closed",
                          label: "text-contrast",
                          value: "text-contrast",
                        },
                      },
                      {
                        key: "appr",
                        label: "Approved",
                        value: brandRow
                          ? safeNum(brandRow.approved) || "-"
                          : "-",
                        classes: { root: "bg-approved" },
                      },
                      {
                        key: "rev",
                        label: "Review",
                        value: brandRow
                          ? safeNum(brandRow.reviewed) || "-"
                          : "-",
                        classes: { root: "bg-review" },
                      },
                      {
                        key: "draft",
                        label: "Draft",
                        value: brandRow ? safeNum(brandRow.draft) || "-" : "-",
                        classes: { root: "bg-low" },
                      },
                    ]}
                    classes={{
                      root: "doc-cards",
                      card: "pa-10",
                      value: "fs-32 text-right w-100",
                      label: "fs-16",
                    }}
                  />
                )}
              </Col>

              <Col style={{ flex: "1 1 0", width: 350 }}>
                <div style={{ width: "100%", height: 200 }}>
                  <DonutChart
                    key={`documenter-overall-${effectiveRangeId}`}
                    chartId="documenter-overall-donut"
                    title="Documentation"
                    subtitle="Documents: "
                    totalCount={totalDocs || "-"}
                    showDateInHeader={true}
                    classes={{ root: "h-200-px" }}
                    datasets={donutDatasets}
                    metric={{
                      label: "Completion",
                      value: completionPct,
                      type: "percent",
                    }}
                  />
                </div>
              </Col>
            </div>
          </ExportSection>

          <div
            style={{
              display: "flex",
              width: "100%",
              padding: "20px",
              gap: "20px",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <ExportSection
              moduleKey={MODULE_KEY}
              index={2}
              exportType="table"
              exportData={{
                title: "Documentation - Department-wise",
                columns: DeptColumns,
                rows: deptWiseRows,
              }}
              style={{
                flex: "1 1 48%",
                minWidth: 400,
                maxWidth: "50%",
                paddingTop: 0,
              }}
            >
              <NMStatsTable
                title="Documentation"
                subTitle="Department-wise"
                columns={DeptColumns}
                dataSource={deptWiseRows}
                showDateInHeader={true}
                getCellTypeRepresentation={(cellInfo) => {
                  if (
                    cellInfo.rowId === -1 &&
                    cellInfo.columnId !== "overall"
                  ) {
                    return "DOT";
                  }
                  return null;
                }}
              />
            </ExportSection>

            <ExportSection
              moduleKey={MODULE_KEY}
              index={3}
              exportType="chart"
              style={{
                flex: "1 1 48%",
                minWidth: 400,
                maxWidth: "50%",
                paddingTop: 0,
              }}
            >
              <div
                className="mt-12 chart-export-pad"
                style={{ width: "100%", overflow: "visible" }}
              >
                <div style={{ width: "100%", height: 500 }}>
                  <BarChart
                    key={`documenter-departments-${effectiveRangeId}`}
                    title="Documentation"
                    subtitle="Department-wise"
                    label={{ xAxis: "Documents", yAxis: "Department" }}
                    datasets={chartDatasets}
                    stackType="normal"
                    // classes={{ root: `h-${barChartHeight}-px w-100` }}
                    classes={{ root: "h-500-px w-100" }}
                    showDateInHeader={true}
                  />
                </div>
              </div>
            </ExportSection>
          </div>

          <div style={{ paddingTop: "100px" }}>
            <hr className="nm-hr nm-hr--center" />
          </div>
        </div>
      )}
      {mode === "fusion" && (
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "flex-start",
            flexWrap: "wrap",
            padding: "20px 0 0 10px",
          }}
        >
          <ExportSection
            moduleKey={MODULE_KEY}
            index={1}
            exportType={mode === "export_everything" ? "chart" : "table"}
            exportData={{
              title: "Documentation",
              columns: DonutColumns,
              rows: DonutRows,
            }}
            style={{
              display: "inline-block",
              width: 350,
              flex: "0 0 auto",
            }}
          >
            <Col style={{ flex: "1 1 0", width: 350 }}>
              <div style={{ width: "100%", height: 200 }}>
                <DonutChart
                  key={`documenter-fusion-${effectiveRangeId}`}
                  chartId="documenter-overall-donut"
                  title="Documentation"
                  subtitle="Documents: "
                  totalCount={totalDocs || "-"}
                  classes={{ root: "h-200-px" }}
                  datasets={donutDatasets}
                  metric={{
                    label: "Completion",
                    value: completionPct,
                    type: "percent",
                  }}
                />
              </div>
            </Col>
          </ExportSection>
        </div>
      )}
    </>
  );
}

const tranformDataIntoTable = (res, user, departments) => {
  const brandname = user.org.brandname;
  if (
    !res ||
    !res.department_status ||
    Object.keys(res.department_status).length === 0
  )
    return [];

  const deptStatus = res?.department_status || {};
  let output = (departments || [])
    .map((deptObj) => {
      const deptName = deptObj.id ?? deptObj.name;
      const stats = deptStatus[deptName] || {};
      return {
        department: deptName,
        overall: stats.total || 0,
        published: stats.published || 0,
        approved: stats.approved || 0,
        reviewed: stats.reviewed || 0,
        draft: stats.draft || 0,
      };
    })
    .filter((row) => {
      // Only include departments with at least one non-zero value
      const numericFields = [
        "overall",
        "published",
        "approved",
        "reviewed",
        "draft",
      ];
      return numericFields.some((field) => row[field] !== 0);
    });

  let totalRow = {
    department: brandname,
    overall: 0,
    published: 0,
    approved: 0,
    reviewed: 0,
    draft: 0,
    highlight: true,
  };

  for (let r of output) {
    totalRow.overall += r.overall || 0;
    totalRow.published += r.published || 0;
    totalRow.approved += r.approved || 0;
    totalRow.reviewed += r.reviewed || 0;
    totalRow.draft += r.draft || 0;
  }

  output.sort((a, b) =>
    a.department.toLowerCase().localeCompare(b.department.toLowerCase())
  );
  output.unshift(totalRow);

  output.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key !== "department" && row[key] === 0) row[key] = "-";
    });
  });

  return output;
};
